import React, { useEffect } from 'react';

import { authenticateShopperEmailMailbox, getShopperEmail } from 'apis/ShopRequestAPI';
import { useRootContext } from 'components/layout/RootContext';
import { getErrorMessageByPriority } from 'helpers/emails';

type MailboxActionShopperEmail = {
  id?: number;
};

type UseShopperEmailsMailboxActionsOptions = {
  authenticateMailbox?: (shopperEmailId: number) => Promise<{ auth_url: string }>;
  isMailboxActive?: (shopperEmailId: number) => Promise<boolean>;
};

const MAILBOX_AUTH_POLL_INTERVAL_MS = 1000;
const MAILBOX_AUTH_POLL_MAX_ATTEMPTS = 10;

const wait = (timeoutMs: number) =>
  new Promise((resolve) => {
    window.setTimeout(resolve, timeoutMs);
  });

export const useShopperEmailsMailboxActions = (
  refetchShopperEmails: () => Promise<unknown>,
  options?: UseShopperEmailsMailboxActionsOptions
) => {
  const { showMessage } = useRootContext();
  const [authenticatingEmailId, setAuthenticatingEmailId] = React.useState<number | null>(null);
  const authWindowCloseMonitorRef = React.useRef<number | null>(null);
  const authPopupWindowRef = React.useRef<Window | null>(null);

  const clearAuthPopupMonitor = React.useCallback(() => {
    if (authWindowCloseMonitorRef.current) {
      window.clearInterval(authWindowCloseMonitorRef.current);
      authWindowCloseMonitorRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      clearAuthPopupMonitor();
    };
  }, [clearAuthPopupMonitor]);

  const authenticateMailbox =
    options?.authenticateMailbox ?? ((shopperEmailId: number) => authenticateShopperEmailMailbox(shopperEmailId));

  const resolveMailboxLinkResult = React.useCallback(
    async (shopperEmailId: number) => {
      for (let attempt = 0; attempt < MAILBOX_AUTH_POLL_MAX_ATTEMPTS; attempt += 1) {
        try {
          const isMailboxActive = options?.isMailboxActive
            ? await options.isMailboxActive(shopperEmailId)
            : (await getShopperEmail(shopperEmailId))?.mailbox_connection_status === 'active';

          if (isMailboxActive) {
            await refetchShopperEmails();
            showMessage('success', 'Mailbox linked successfully.');
            return;
          }
        } catch (error) {
          if (attempt === MAILBOX_AUTH_POLL_MAX_ATTEMPTS - 1) {
            console.error('Failed to verify shopper mailbox link after OAuth callback.', error);
          }
        }

        await wait(MAILBOX_AUTH_POLL_INTERVAL_MS);
      }

      await refetchShopperEmails();
    },
    [options?.isMailboxActive, refetchShopperEmails, showMessage]
  );

  const handleMailboxAction = React.useCallback(
    async (shopperEmail: MailboxActionShopperEmail) => {
      if (!shopperEmail.id) {
        return;
      }
      const shopperEmailId = shopperEmail.id;

      const popup = window.open('', 'shopper-mailbox-auth', 'width=520,height=720');

      if (!popup) {
        showMessage('error', 'Unable to open mailbox authentication window.');
        return;
      }

      popup.document.title = 'Connecting mailbox...';
      popup.document.body.textContent = 'Connecting mailbox...';

      setAuthenticatingEmailId(shopperEmailId);

      try {
        const { auth_url: authUrl } = await authenticateMailbox(shopperEmailId);
        popup.location.href = authUrl;

        authPopupWindowRef.current = popup;
        clearAuthPopupMonitor();

        authWindowCloseMonitorRef.current = window.setInterval(() => {
          if (!authPopupWindowRef.current || authPopupWindowRef.current.closed) {
            clearAuthPopupMonitor();
            authPopupWindowRef.current = null;
            setAuthenticatingEmailId(null);
            resolveMailboxLinkResult(shopperEmailId).catch(() => {
              showMessage('error', 'Failed to refresh shopper contact after mailbox authentication.');
            });
          }
        }, 500);
      } catch (error: any) {
        popup.close();
        setAuthenticatingEmailId(null);
        showMessage(
          'error',
          getErrorMessageByPriority(error?.response?.data, 'Failed to start mailbox authentication.', ['detail'])
        );
      }
    },
    [authenticateMailbox, clearAuthPopupMonitor, resolveMailboxLinkResult, showMessage]
  );

  return {
    authenticatingEmailId,
    handleMailboxAction,
  };
};
